import { HttpClient } from "@infra/HttpClient";
import type { PersonService } from "@/domain/port/PersonService";
import type { CreatePersonCommand, PersonDto } from "./dto/PersonDto";
import {
  personFromDto,
  updatePersonRequestFromCommand,
} from "./mappers/PersonMapper";
import type { Person, UpdatePersonCommand } from "@/domain/model/Person";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { PersonId } from "@/domain/model/Ids";

export class PersonServiceImpl implements PersonService {
  constructor(private readonly http: HttpClient) {}

  async list(q: PageQuery): Promise<{ items: Person[]; total: number }> {
    const params = toQueryParams(q);

    const { items, total } = await this.http.request<{
      items: PersonDto[];
      total: number;
    }>(`/api/persons?${params.toString()}`);

    return { items: items.map(personFromDto), total };
  }

  async create(cmd: CreatePersonCommand): Promise<Person> {
    const res = await this.http.request<PersonDto>("/api/persons", {
      method: "POST",
      body: JSON.stringify(cmd),
    });

    return personFromDto(res);
  }

  async getById(id: PersonId): Promise<Person> {
    const dto = await this.http.request<PersonDto>(
      `/api/persons/${encodeURIComponent(String(id))}`,
    );
    return personFromDto(dto);
  }

  async update(id: PersonId, cmd: UpdatePersonCommand): Promise<Person> {
    const req = updatePersonRequestFromCommand(cmd);
    const dto = await this.http.request<PersonDto>(
      `/api/persons/${encodeURIComponent(String(id))}`,
      {
        method: "PUT",
        body: JSON.stringify(req),
      },
    );
    return personFromDto(dto);
  }

  async delete(id: PersonId): Promise<Person> {
    const dto = await this.http.request<PersonDto>(
      `/api/persons/${encodeURIComponent(String(id))}`,
      { method: "DELETE" },
    );
    return personFromDto(dto);
  }
}

function isNonEmptyString(x: unknown): x is string {
  return typeof x === "string" && x.trim().length > 0;
}

function flattenPersonFilter(filter: any): Record<string, string> {
  const out: Record<string, string> = {};

  if (isNonEmptyString(filter?.q)) out.q = filter.q.trim();

  // BooleanInput usually yields true/false, but can also be undefined.
  if (typeof filter?.hasUser === "boolean")
    out.hasUser = String(filter.hasUser);

  return out;
}

function toQueryParams(q: PageQuery): URLSearchParams {
  const params = new URLSearchParams({
    page: String(q.page),
    pageSize: String(q.pageSize),
    sortBy: q.sortBy ?? "displayName",
    sortDir: q.sortDir ?? "asc",
  });

  const flat = flattenPersonFilter(q.filter);
  for (const [k, v] of Object.entries(flat)) params.set(k, v);

  return params;
}
